import React, { useState, useEffect, useCallback } from 'react';
import { Download, ZoomIn, ZoomOut, RotateCw, X } from 'lucide-react';
import { getDocumentURL } from '../../services/firebase';

interface DocumentViewerProps {
  documentPath: string;
  documentName: string;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentPath,
  documentName,
  onClose
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  const loadDocument = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Check if the documentPath is already a full URL
      if (documentPath.startsWith('http')) {
        // It's already a full URL, use it directly
        setImageUrl(documentPath);
      } else {
        // It's a storage path, get the download URL
        const url = await getDocumentURL(documentPath);
        setImageUrl(url);
      }
    } catch (err) {
      setError('Failed to load document');
      console.error('Error loading document:', err);
    } finally {
      setLoading(false);
    }
  }, [documentPath]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  const handleDownload = async () => {
    try {
      // For Firebase Storage URLs, we need to handle CORS restrictions
      // Method 1: Try direct download with proper headers
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = documentName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      
      // Add to DOM and trigger click
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
    } catch (err) {
      console.error('Error downloading document:', err);
      // Fallback: Open in new tab for manual download
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.25));
  const rotate = () => setRotation(prev => (prev + 90) % 360);
  const reset = () => {
    setScale(1);
    setRotation(0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{documentName}</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              title="Download"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-center items-center space-x-4 p-4 bg-gray-50 border-b">
          <button
            onClick={zoomOut}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
            title="Zoom Out"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-600 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
            title="Zoom In"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <button
            onClick={rotate}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
            title="Rotate"
          >
            <RotateCw className="h-5 w-5" />
          </button>
          <button
            onClick={reset}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
          >
            Reset
          </button>
        </div>

        {/* Document Viewer */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-2">{error}</p>
                <button
                  onClick={loadDocument}
                  className="btn-primary"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div
                className="relative"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transformOrigin: 'center',
                  transition: 'transform 0.2s ease-in-out'
                }}
              >
                <img
                  src={imageUrl}
                  alt={documentName}
                  className="max-w-full h-auto shadow-lg"
                  style={{ maxHeight: '60vh' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer; 